import React, { useCallback } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import AsyncSection from "../common/AsyncSection";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import ScheduleDetailBody, { ScheduleDetailBadges } from "./ScheduleDetailBody";

/**
 * The /schedules/:scheduleId route — a deep link. Day-to-day the detail opens
 * in a modal from the Schedules tab; this page exists so a URL can be shared.
 * Both render ScheduleDetailBody, so they cannot drift.
 */
export default function ScheduleDetail() {
  const { userId, scheduleId } = useParams();
  const { deviceIndex } = useOutletContext();
  const basePath = `/admin/dash/users/${userId}`;

  const fetcher = useCallback(
    async () => ({ items: await adminUserService.fetchUserSchedule(userId, scheduleId) }),
    [userId, scheduleId]
  );

  const { data, state, error, refetch } = useAdminResource(fetcher, [userId, scheduleId]);
  const schedule = data?.schedule ?? null;

  return (
    <div>
      <Link
        to={`${basePath}/schedules`}
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        <FiArrowLeft size={16} /> Schedules
      </Link>

      <AsyncSection
        state={state}
        error={error}
        onRetry={() => refetch()}
        isEmpty={!schedule}
        loadingMessage="Loading schedule…"
        emptyTitle="Schedule not found"
        emptyMessage="It may have been deleted."
      >
        {schedule && (
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                  {schedule.name}
                  {schedule.smartRestore && (
                    <span title="Smart Restore is on" className="text-indigo-500">✨</span>
                  )}
                </h2>
                <p className="mt-1 font-mono text-xs text-slate-400">
                  {schedule.scheduleId || schedule._id}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ScheduleDetailBadges schedule={schedule} />
              </div>
            </div>

            <ScheduleDetailBody schedule={schedule} deviceIndex={deviceIndex} />
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
